import React from 'react'
import { Link } from 'react-router-dom'
import './../styles/Home.css'
import './../styles/CardComponent.css'
import ContactForm from './ContactForm'
import EmblaCarousel from './EmblaCarousel'
import CardComponent from './CardComponent'
import OurServices from './OurServices'
import HeroAnimation from './HeroAnimation'
import SEO from './SEO'
import { trackCtaClick } from '../utils/analytics'

const Home = () => (
  <>
    <SEO
      path="/"
      title="QA Tech Ltd, London — Marketing & Software Engineering"
      description="QA Tech Ltd is a London-based marketing and engineering consultancy. We build custom websites from scratch, run SEO and Google Ads, and deliver expert software testing and app development — helping ambitious businesses grow."
      keywords="QA Tech, QA Tech Ltd, QA Tech Consultancy, qatechltd, marketing agency London, SEO services London, Google Ads London, custom website development London, software testing London, QA consultancy London, app development London, web development London, marketing services UK, software engineering UK"
      jsonLd={{
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': 'https://qatechltd.com/#organization',
            name: 'QA Tech',
            alternateName: ['QA Tech Ltd', 'QA Tech Consultancy', 'qatechltd'],
            legalName: 'QA Tech Ltd',
            url: 'https://qatechltd.com',
            logo: {
              '@type': 'ImageObject',
              url: 'https://qatechltd.com/favicon-96x96.png',
            },
            sameAs: [
              'https://www.linkedin.com/company/qa-tech-consultancy',
              'https://find-and-update.company-information.service.gov.uk/',
            ],
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'info@qatechltd.co.uk',
              contactType: 'Sales',
              areaServed: 'GB',
              availableLanguage: ['en'],
            },
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'GB',
              addressLocality: 'London',
            },
          },
          {
            '@type': 'ProfessionalService',
            '@id': 'https://qatechltd.com/#service',
            name: 'QA Tech Ltd',
            url: 'https://qatechltd.com',
            image: 'https://qatechltd.com/favicon-96x96.png',
            priceRange: '££',
            description:
              'Software testing, QA, app and web development, technical resourcing and marketing services for ambitious businesses.',
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'GB',
              addressLocality: 'London',
            },
            areaServed: { '@type': 'Country', name: 'United Kingdom' },
            parentOrganization: { '@id': 'https://qatechltd.com/#organization' },
          },
          {
            '@type': 'WebSite',
            '@id': 'https://qatechltd.com/#website',
            url: 'https://qatechltd.com',
            name: 'QA Tech',
            publisher: { '@id': 'https://qatechltd.com/#organization' },
            inLanguage: 'en-GB',
          },
        ],
      }}
    />
    <div className="body">
      <div className="home">
        <div className="content">
          <span className="eyebrow">QA Tech &mdash; Software Quality &amp; Engineering</span>
          <h1>Building Confidence Through Quality</h1>
          <p className="hero-sub">
            Expert testing, app development and engineering teams that help
            ambitious businesses ship with confidence.
          </p>
          <div className="hero-cta-row">
            <a
              href="#contact-form"
              className="get-in-touch-button"
              onClick={() => trackCtaClick('Get in touch', 'home_hero')}
            >
              Get in touch
            </a>
            <Link
              to="/marketing"
              className="hero-secondary-link"
              onClick={() => trackCtaClick('Marketing services', 'home_hero')}
            >
              Marketing services &rarr;
            </Link>
          </div>
        </div>
        <div className="image-container">
          <HeroAnimation />
        </div>
      </div>

      {/* Who we are — short, opinionated intro with USPs */}
      <section className="who-we-are" aria-labelledby="who-we-are-h">
        <div className="who-we-are__inner">
          <span className="eyebrow">Who we are</span>
          <h2 id="who-we-are-h" className="who-we-are__title">
            Engineers and marketers, <span className="text-gradient">under one roof.</span>
          </h2>
          <p className="who-we-are__body">
            QA Tech Ltd is a London-based marketing and engineering
            consultancy. For over a decade we&rsquo;ve built and tested
            software for some of the biggest companies in the world.
            Today we bring that same craft to ambitious businesses who want
            both engineering rigour <em>and</em> marketing that actually
            performs &mdash; without paying consultancy prices.
          </p>

          <div className="who-we-are__usps">
            <article className="who-usp">
              <span className="who-usp__num" aria-hidden="true">01</span>
              <h3 className="who-usp__title">Engineers, not marketers</h3>
              <p>
                Built by people who actually understand how the technology
                works &mdash; because we&rsquo;ve spent careers building it.
              </p>
            </article>
            <article className="who-usp">
              <span className="who-usp__num" aria-hidden="true">02</span>
              <h3 className="who-usp__title">Both sides. One team.</h3>
              <p>
                Marketing and engineering, under the same roof. No
                translation tax, no vendor hand-offs &mdash; the same people
                in every meeting.
              </p>
            </article>
            <article className="who-usp">
              <span className="who-usp__num" aria-hidden="true">03</span>
              <h3 className="who-usp__title">Big-company craft</h3>
              <p>
                Over a decade building for some of the biggest companies in
                the world. The same standard, delivered at a fraction of
                the consultancy price.
              </p>
            </article>
          </div>
        </div>
      </section>

      <div className="cards-container">
        <CardComponent
          title="App Development"
          body="Full-stack application development with comprehensive testing from conception to deployment"
          icon="development-alt"
          to="/services/app-development"
        />
        <CardComponent
          title="Software Testing"
          body="Expert QA services including automated testing, performance testing, and quality assurance"
          icon="testing-alt"
          to="/services/software-testing"
        />
        <CardComponent
          title="Technical Resourcing"
          body="Skilled QA engineers and developers at all levels to scale your development teams"
          icon="resourcing-alt"
          to="/services/technical-resourcing"
        />
        <CardComponent
          title="DevOps & CI/CD"
          body="Streamlined deployment pipelines with automated testing and continuous integration processes"
          icon="devops-alt"
          to="/services/devops-cicd"
        />
        <CardComponent
          title="Web Development"
          body="Modern responsive websites and web applications with comprehensive testing and optimization"
          icon="web-alt"
          to="/services/web-development"
        />
        <CardComponent
          title="Mobile Development"
          body="Native and cross-platform mobile applications with thorough testing for iOS and Android"
          icon="mobile-alt"
          to="/services/mobile-development"
        />
        <CardComponent
          featured
          title="Marketing & Growth"
          body="Websites built from scratch, SEO, full Google setup (Analytics, Tag Manager, Search Console, Business Profile, Ads), social media management and full company rebrands — designed to help businesses grow."
          icon="marketing"
          to="/marketing"
          ctaLabel="Explore marketing services"
        />
      </div>
      <OurServices />

      {/* Featured case study callout */}
      <section className="home-featured" aria-labelledby="home-featured-h">
        <Link
          to="/case-studies/puma"
          className="home-featured__card"
          onClick={() => trackCtaClick('Featured case study — Puma', 'home_featured')}
        >
          <div className="home-featured__inner">
            <div className="home-featured__text">
              <span className="eyebrow">Featured case study</span>
              <h2 id="home-featured-h">
                Puma &mdash; first mobile app, full rebrand,{' '}
                <span className="text-gradient">global launch.</span>
              </h2>
              <p>
                We led testing and automation, and supported the marketing
                team with Sanity Studio component development, on
                Puma&rsquo;s biggest digital programme to date &mdash; a
                global e-commerce relaunch and the company&rsquo;s
                first-ever mobile app &mdash; across Europe, the UK, North
                America, South Asia and the Middle East. Multi-processor
                payment validation and a fully autonomous
                merge-to-production pipeline included.
              </p>
              <span className="home-featured__cta">
                Read the Puma case study <span aria-hidden="true">&rarr;</span>
              </span>
            </div>
          </div>
        </Link>

        <div className="home-featured__more">
          <Link
            to="/case-studies"
            onClick={() => trackCtaClick('Read more case studies', 'home_featured_more')}
          >
            Read more of our case studies <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </section>

      <div className="carousel-container">
        <h2 style={{ color: 'black' }}>Who we've worked with</h2>
        <EmblaCarousel />
      </div>
      <div>
        <ContactForm />
      </div>
    </div>
  </>
)

export default Home
